import React from 'react';
import { useParams } from 'react-router';
import { useMutation, useQuery } from '@apollo/client';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import ConfirmationModal from '../../components/ConfirmationModal';
import { gql } from '../../graphql/generated/gql';
import { UIActions } from '../../ui/uiSlice';
import { legacyUpdateQueryList } from '../../utils/graphql/updateQueryList';
import useNavigate from '../../utils/router/useNavigate';
import AdminProvisionForm from './AdminProvisionForm';

gql(`
  fragment AdminProvisionEditView_provision on Provision {
    ...AdminProvisionForm_provision
    isSsoDefault
  }
`);

const ADMIN_PROVISION_EDIT_VIEW_QUERY = gql(/** @complexity 12 */ `
  query AdminProvisionEditView {
    session: currentSession {
      organization {
        id
        provisions {
          ...AdminProvisionEditView_provision
        }
      }
    }
  }
`);

const DELETE_PROVISION_MUTATION = gql(/** @complexity 2 */ `
  mutation deleteProvision($id: ID!) {
    deleteProvision(id: $id) {
      id
    }
  }
`);

const UPDATE_PROVISION_MUTATION = gql(/** @complexity 10 */ `
  mutation updateProvision(
    $id: ID!
    $name: String
    $type: ProvisionType
    $urlEnabled: Boolean
    $favoriteIds: [ID!]
    $memberIds: [ID!]
  ) {
    updateProvision(
      id: $id
      name: $name
      type: $type
      urlEnabled: $urlEnabled
      favoriteIds: $favoriteIds
      memberIds: $memberIds
    ) {
      ...AdminProvisionEditView_provision
    }
  }
`);

const REGENERATE_PROVISION_SECRET_MUTATION = gql(/** @complexity 2 */ `
  mutation regenerateProvisionSecret($id: ID!) {
    regenerateProvisionSecret(id: $id) {
      id
      secret
    }
  }
`);

const AdminProvisionEditView = () => {
  const navigate = useNavigate();
  const { id } = useParams() as { id: string };
  const dispatch = useAppDispatch();

  const { data } = useQuery(ADMIN_PROVISION_EDIT_VIEW_QUERY);

  const provisions = data?.session?.organization?.provisions ?? [];
  const provision = provisions.find((p) => p?.id === id);

  const [deleteProvision] = useMutation(DELETE_PROVISION_MUTATION);
  const [regenerateProvisionSecret] = useMutation(
    REGENERATE_PROVISION_SECRET_MUTATION,
  );
  const [updateProvision] = useMutation(UPDATE_PROVISION_MUTATION);

  if (!provision) {
    return null;
  }

  const handleDelete = (onError: (e: Error) => unknown) => {
    dispatch(
      UIActions.openModal(ConfirmationModal, {
        title: 'Remove this provision?',
        description: provision.isSsoDefault
          ? 'This is the default provision. You can choose a new default from the Authentication settings.'
          : null,
        confirmButton: {
          text: 'Delete',
          onClick: () =>
            deleteProvision({
              variables: { id },
              update: legacyUpdateQueryList(
                { query: ADMIN_PROVISION_EDIT_VIEW_QUERY },
                'session.organization.provisions',
                'deleteProvision',
                false,
              ),
            })
              .then(() => {
                navigate('/admin/provisions');
              })
              .catch((e: Error) => onError(e)),
        },
      }),
    );
  };

  const handleRegenerateUrl = () => {
    dispatch(
      UIActions.openModal(ConfirmationModal, {
        title: 'Regenerate link?',
        description:
          'Your current URL will no longer work and will be replaced by a new URL.',
        confirmButton: {
          text: 'Regenerate',
          onClick: () => regenerateProvisionSecret({ variables: { id } }),
        },
      }),
    );
  };

  return (
    <AdminProvisionForm
      provision={provision}
      onDelete={handleDelete}
      onRegenerateUrl={handleRegenerateUrl}
      onSubmit={(values) => {
        return updateProvision({
          variables: {
            id,
            ...values,
          },
        });
      }}
    />
  );
};

export default AdminProvisionEditView;
